import { get } from 'lodash';
import i18next from 'i18next';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { relativeRealFromTheoreticalRatio } from '@commons/utils/math';

import { group as groupService } from '@services/group';

import accountService from '@services/account';
import orderService from '@services/order';

const fetchLocationsOfAccount = async (accountId, showMessage) => {
  try {
    const locations = await accountService.getLocationsOfAccount(accountId);

    return locations;
  } catch (err) {
    showMessage(i18next.t('HOME.ACTIVITY_REPORT_FETCH_LOCATIONS_FAILURE'), 'error');

    return [];
  }
};

const fetchGroupsByStoreIds = async (storeIds, showMessage) => {
  const formattedGroups = {};

  try {
    const groupMappings = await groupService.getGroupsOfStores(storeIds);

    groupMappings.forEach((groupMapping) => {
      const groupId = groupMapping.lnkGroupStoregroupmappingrel.id;
      const groupName = groupMapping.lnkGroupStoregroupmappingrel.name;

      if (!formattedGroups[groupId]) {
        formattedGroups[groupId] = {
          groupId,
          storeIds: [],
          name: groupName,
          id: groupMapping.id,
        };
      }

      formattedGroups[groupId].storeIds.push(groupMapping.storeId);
    });
  } catch (err) {
    showMessage(i18next.t('GENERAL.GROUPS_CLIENT_FETCH_FAILURE'), 'error');

    return {};
  }

  return formattedGroups;
};

const _formatRow = (row, index) => {
  const lossTotal = get(row, 'losses.total', 0);
  const foodCost = get(row, 'foodCost.realConsumption', 0);
  const foodCostGapFromTheorical = get(row, 'foodCost.gapFromTheoretical', 0);

  const materialRatio = !row.turnover ? null : (row.theoricalConsumption / row.turnover) * 100;

  const orderOverTurnoverRatio = !row.turnover ? null : (foodCost / row.turnover) * 100;

  const realFromTheoricalRatio = relativeRealFromTheoreticalRatio(
    foodCostGapFromTheorical,
    row.theoricalConsumption,
  );

  const lossRatio = !row.turnover ? null : (lossTotal / row.turnover) * 100;

  return {
    store: { name: i18next.t('ORDERS.BY_CATEGORY.LABEL_TOTAL') },
    ...row,
    id: index,
    // Computed data
    lossRatio,
    materialRatio,
    realFromTheoricalRatio,
    orderOverTurnoverRatio,
  };
};

const fetchAnalyticsData = async (
  storeIds,
  startDate,
  endDate,
  { withNonSalesConsumption = true } = {},
) => {
  const analytics = await orderService.getOrdersAnalyticsByStore(
    storeIds,
    startDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    endDate.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY),
    { withNonSalesConsumption },
  );

  const formattedData = analytics.data.map(_formatRow);

  const totalRow = _formatRow(analytics.totalData, 'total');

  return {
    total: totalRow,
    data: formattedData,
  };
};

/**
 * Compute the total row of the analytics data
 * Same method as getAnalyticsByStoreStockEvents service in API codebase
 *
 * @param {Array} data - The analytics data
 *
 * @returns {Object} The total row
 */
const computeAndFormatTotalRow = (data) => {
  const computedData = data.reduce(
    (result, item) => {
      if (item.turnover != null) {
        result.turnover += item.turnover;
      }

      if (item.grossSalary != null) {
        result.grossSalary += item.grossSalary;
      }

      if (item.theoricalConsumption != null) {
        result.theoricalConsumption += item.theoricalConsumption;
      }

      if (item.inventoryShrinkage) {
        result.inventoryShrinkage += item.inventoryShrinkage;
      }

      Object.keys(item.foodCost).forEach((propertyKey) => {
        const value = item.foodCost[propertyKey];

        if (value != null) {
          result.foodCost[propertyKey] += value;
        }
      });

      Object.keys(item.losses).forEach((propertyKey) => {
        const value = item.losses[propertyKey];

        if (value != null) {
          result.losses[propertyKey] += value;
        }
      });

      return result;
    },
    {
      losses: {
        product: null,
        supplierProduct: null,
        total: null,
      },
      foodCost: {
        order: null,
        rushOrder: null,
        orderTotal: null,
        transferIn: null,
        transferOut: null,
        transferTotal: null,
        finalStock: null,
        initialStock: null,
        stockVariation: null,
        realConsumption: null,
        gapFromTheoretical: null,
      },
      turnover: null,
      grossSalary: null,
      theoricalConsumption: 0,
      inventoryShrinkage: null,
    },
  );

  return _formatRow(computedData, 'total');
};

export default {
  fetchAnalyticsData,
  fetchGroupsByStoreIds,
  fetchLocationsOfAccount,
  computeAndFormatTotalRow,
};
